import { getCurrentInstance } from "vue";

export interface ChannelHandlers<TMessage = unknown> {
  connected?: () => void;
  rejected?: () => void;
  received: (message: TMessage) => void;
}

export interface ChannelSubscription {
  subscribe: (params?: Record<string, unknown>) => void;
  unsubscribe: () => void;
  perform: (action: string, data?: Record<string, unknown>) => void;
}

const noop = (): void => {};

export function useChannel<TMessage = unknown>(
  channel: string,
  handlers: ChannelHandlers<TMessage>,
): ChannelSubscription {
  const instance = getCurrentInstance();
  if (!instance?.proxy) {
    throw new Error("useChannel must be called within a component or store setup");
  }

  const cable = instance.proxy.$cable;

  const subscribe = (params: Record<string, unknown> = {}): void => {
    cable.subscribe({ channel, ...params });
    cable._addChannel(
      channel,
      {
        connected: handlers.connected ?? noop,
        rejected: handlers.rejected ?? noop,
        received: handlers.received as (message: unknown) => void,
      },
      { _uid: instance.uid, _value: instance.proxy },
    );
  };

  const unsubscribe = (): void => cable._removeChannel(channel, instance.uid);

  const perform = (action: string, data: Record<string, unknown> = {}): void =>
    cable.perform({ channel, action, data });

  return { subscribe, unsubscribe, perform };
}
